// OSIS - Portal Angajați - Adaptat pentru echipa OSIS (11 angajați)
import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MapPin, Calendar, Award, Download, Edit3, Save, X, 
  Clock, TrendingUp, Target, Star, FileText, Printer, Share2, Eye, 
  Factory, Wrench, Users 
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData, getDepartmentColor, getStatusColor } from '../../utils/mockData';

// Toast Component pentru notificări
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'i'
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-in`}>
      <span className="text-xl">{icons[type]}</span>
      <span>{message}</span>
      <button onClick={onClose} className="ml-auto">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

const PortalAngajati = () => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(1); // Ciprian Aostăcioaei implicit
  const [employee, setEmployee] = useState(mockData.employees.find(e => e.id === selectedEmployeeId));
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toasts, showToast, removeToast } = useToast();

  // Actualizare angajat la schimbarea selecției
  useEffect(() => {
    const selectedEmployee = mockData.employees.find(e => e.id === selectedEmployeeId);
    setEmployee(selectedEmployee);
  }, [selectedEmployeeId]);

  const handleEditStart = () => {
    setEditForm({
      email: employee.email,
      phone: employee.phone,
      skills: employee.skills
    });
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    setLoading(true);
    
    // Simulare API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setEmployee(prev => ({
      ...prev,
      ...editForm
    }));
    
    setIsEditing(false);
    setLoading(false);
    showToast('Profilul a fost actualizat cu succes!', 'success');
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleExportPDF = async (type) => {
    setLoading(true);
    
    // Simulare export PDF
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    showToast(`${type} a fost generat și descărcat cu succes!`, 'success');
    setLoading(false);
    setShowExportModal(false);
  };

  const vacations = mockData.vacations.filter(v => v.employee === employee.name);
  const totalVacationDays = 25;
  const usedDays = vacations.reduce((acc, v) => acc + v.days, 0);
  const remainingDays = totalVacationDays - usedDays;

  // Statistici de performanță pentru angajații OSIS
  const performanceStats = {
    currentGoals: 3,
    completedGoals: 8,
    avgProgress: 85,
    rating: employee.department === 'Management' ? 4.9 : 
            employee.department === 'Producție' ? 4.5 :
            employee.department === 'Tehnică' ? 4.7 : 4.2
  };

  const getDepartmentIcon = (department) => {
    switch(department) {
      case 'Management': return Award;
      case 'Producție': return Factory;
      case 'Tehnică': return Target;
      case 'Montaj': return Wrench;
      case 'Logistică': return MapPin;
      default: return User;
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header cu selector angajați */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-4">
            <Factory className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Portal Angajați OSIS
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Sistem de management resurse umane - pardoseli industriale
              </p>
            </div>
          </div>

          {/* Selector angajat */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Angajat:
            </label>
            <select 
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(parseInt(e.target.value))}
              className="form-input min-w-[300px]"
            >
              {mockData.employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} - {emp.position}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Rapoarte
          </button>
          
          {isEditing ? (
            <div className="flex gap-2">
              <button 
                onClick={handleEditSave}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salvează
              </button>
              <button 
                onClick={handleEditCancel}
                className="btn-secondary flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Anulează
              </button>
            </div>
          ) : (
            <button 
              onClick={handleEditStart}
              className="btn-primary flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Editează Profil
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profil Personal */}
        <div className="lg:col-span-2">
          <Card title="Profil Personal" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Nume complet</p>
                    <p className="font-semibold">{employee.name}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {React.createElement(getDepartmentIcon(employee.department), { 
                    className: "w-5 h-5 text-gray-400" 
                  })}
                  <div>
                    <p className="text-sm text-gray-500">Poziție</p>
                    <p className="font-semibold">{employee.position}</p>
                    <Badge variant={getDepartmentColor(employee.department)} className="text-xs mt-1">
                      {employee.department}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="form-input"
                      />
                    ) : (
                      <p className="font-semibold">{employee.email}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Target className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Specializare</p>
                    <p className="font-semibold text-sm">{employee.specialization}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="form-input"
                      />
                    ) : (
                      <p className="font-semibold">{employee.phone}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Data angajării</p>
                    <p className="font-semibold">{new Date(employee.startDate).toLocaleDateString('ro-RO')}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Experiență: {Math.floor((new Date() - new Date(employee.startDate)) / (365.25 * 24 * 60 * 60 * 1000))} ani
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <Badge variant={getStatusColor(employee.status)} className="text-xs">
                      {employee.status}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Rating Performanță</p>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= performanceStats.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold">{performanceStats.rating}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Competențe OSIS */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-3">Competențe în industria proiectelor și lucrărilor</p>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Adaugă competențe separate prin virgule"
                    value={editForm.skills?.join(', ') || ''}
                    onChange={(e) => setEditForm({
                      ...editForm, 
                      skills: e.target.value.split(',').map(s => s.trim())
                    })}
                    className="form-input"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {employee.skills?.map((skill, index) => (
                    <Badge key={index} variant="info" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Salariul pentru transparency */}
            <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                    Salariu lunar
                  </p>
                  <p className="text-lg font-bold text-primary-700 dark:text-primary-300">
                    {employee.salary}
                  </p>
                </div>
                <Award className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </Card>

          {/* Statistici Performanță OSIS */}
          <Card title="Performanță OSIS" icon={TrendingUp} className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">{performanceStats.currentGoals}</div>
                <div className="text-sm text-gray-500">Proiecte Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{performanceStats.completedGoals}</div>
                <div className="text-sm text-gray-500">Finalizate luna</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary-600">{Math.round(performanceStats.avgProgress)}%</div>
                <div className="text-sm text-gray-500">Eficiență</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{performanceStats.rating}</div>
                <div className="text-sm text-gray-500">Rating</div>
              </div>
            </div>

            {/* Indicatori specifici departament */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Indicatori {employee.department}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employee.department === 'Producție' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">proiecte produse/zi:</span>
                      <span className="font-semibold">12-15 buc</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Calitate:</span>
                      <span className="font-semibold text-green-600">99.2%</span>
                    </div>
                  </>
                )}
                {employee.department === 'Management' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Proiecte gestionate:</span>
                      <span className="font-semibold">23</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Satisfacție clienți:</span>
                      <span className="font-semibold text-green-600">98%</span>
                    </div>
                  </>
                )}
                {employee.department === 'Montaj' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Instalări/săptămână:</span>
                      <span className="font-semibold">8-12</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Feedback pozitiv:</span>
                      <span className="font-semibold text-green-600">97%</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Concedii */}
          <Card title="Concedii" icon={Calendar}>
            <div className="space-y-4">
              {vacations.length > 0 ? vacations.map((vacation, index) => (
                <div key={vacation.id} className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                      {vacation.type}
                    </span>
                    <Badge variant={vacation.status === 'Planificat' ? 'warning' : vacation.status === 'În curs' ? 'info' : 'success'}>
                      {vacation.status}
                    </Badge>
                  </div>
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    {new Date(vacation.startDate).toLocaleDateString('ro-RO')} - 
                    {new Date(vacation.endDate).toLocaleDateString('ro-RO')}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {vacation.days} zile
                  </p>
                </div>
              )) : (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Nu există concedii înregistrate</p>
                </div>
              )}
              
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span>Zile totale</span>
                  <span className="font-semibold">{totalVacationDays} zile</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Zile utilizate</span>
                  <span className="font-semibold">{usedDays} zile</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Zile rămase</span>
                  <span className="font-semibold text-green-600">{remainingDays} zile</span>
                </div>
              </div>
              
              <button 
                onClick={() => setShowVacationModal(true)}
                className="w-full btn-primary"
              >
                Solicită Concediu
              </button>
            </div>
          </Card>

          {/* Program Lucru OSIS */}
          <Card title="Program de Lucru OSIS" icon={Clock}>
            <div className="space-y-2 text-sm">
              {Object.entries(employee.workingHours || {}).map(([day, hours]) => (
                <div key={day} className="flex justify-between">
                  <span className="capitalize">
                    {day === 'monday' ? 'Luni' :
                     day === 'tuesday' ? 'Marți' :
                     day === 'wednesday' ? 'Miercuri' :
                     day === 'thursday' ? 'Joi' :
                     day === 'friday' ? 'Vineri' :
                     day === 'saturday' ? 'Sâmbătă' :
                     'Duminică'}
                  </span>
                  <span className={hours === 'Liber' || hours === 'Concediu' ? 'text-green-600' : 'font-medium'}>
                    {hours}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Notă pentru Noemia în concediu maternal */}
            {employee.status === 'Concediu maternal' && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Status special:</strong> Concediu creștere copil până în ianuarie 2025
                </p>
              </div>
            )}
          </Card>

          {/* Echipa OSIS */}
          <Card title="Echipa OSIS" icon={Users}>
            <div className="space-y-3">
              <div className="text-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <Users className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <p className="font-semibold text-primary-700 dark:text-primary-300">
                  {mockData.employees.length} angajați
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-400">
                  {mockData.employees.filter(e => e.status === 'Activ').length} activi
                </p>
              </div>
              
              <div className="space-y-2">
                {Object.entries(
                  mockData.employees.reduce((acc, emp) => {
                    acc[emp.department] = (acc[emp.department] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([dept, count]) => (
                  <div key={dept} className="flex justify-between text-sm">
                    <span>{dept}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Acțiuni Rapide */}
          <Card title="Documente OSIS">
            <div className="space-y-3">
              <button 
                onClick={() => handleExportPDF('Adeverință de Salariat OSIS')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Adeverință Salariat</span>
                </div>
                <span className="text-xs text-gray-500">PDF</span>
              </button>
              
              <button 
                onClick={() => handleExportPDF('Fluturaș Salariu OSIS')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Fluturaș Salariu</span>
                </div>
                <span className="text-xs text-gray-500">Martie 2024</span>
              </button>
              
              <button 
                onClick={() => handleExportPDF('Raport Pontaj OSIS')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Printer className="w-4 h-4" />
                  <span className="text-sm font-medium">Raport Pontaj</span>
                </div>
                <span className="text-xs text-gray-500">Luna curentă</span>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal Export */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Rapoarte OSIS"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Selectează tipul de raport pe care vrei să îl exportezi pentru {employee.name}:
          </p>
          
          <div className="space-y-2">
            {[
              'Adeverință de Salariat OSIS',
              'Fluturaș Salariu - Martie 2024',
              'Raport Pontaj - Luna curentă',
              'Istoric Concedii 2024',
              'Evaluare Performanță OSIS',
              'Certificat de Lucru - Industria proiectelor'
            ].map((reportType) => (
              <button
                key={reportType}
                onClick={() => handleExportPDF(reportType)}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">{reportType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">PDF</span>
                  <Download className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowExportModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Solicită Concediu */}
      <Modal
        isOpen={showVacationModal}
        onClose={() => setShowVacationModal(false)}
        title="Solicită Concediu"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipul concediului</label>
            <select className="form-input">
              <option>Concediu de odihnă</option>
              <option>Concediu medical</option>
              <option>Zi liberă</option>
              <option>Concediu fără plată</option>
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Data început</label>
              <input type="date" className="form-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Data sfârșit</label>
              <input type="date" className="form-input" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Motiv (opțional)</label>
            <textarea 
              className="form-input"
              rows="3"
              placeholder="Motivul solicitării concediului..."
            />
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Zile disponibile:</strong> {remainingDays} zile din {totalVacationDays}
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowVacationModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button 
              className="btn-primary"
              onClick={() => {
                showToast('Solicitarea de concediu a fost trimisă către management!', 'success');
                setShowVacationModal(false);
              }}
            >
              Trimite Solicitarea
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PortalAngajati;